import React, { useMemo } from 'react';

import { Section, Grid } from '@gogame/components';
import { withForm, FormField, SidePageV2 } from '@gogame/form';

import ChannelsFormComponent from 'scenes/Gallery/GalleryPage/Creatives/components/ChannelsField';
import ColorFormComponent from 'scenes/Gallery/GalleryPage/Creatives/components/ColorField';
import CreativePreview from 'scenes/Gallery/GalleryPage/Creatives/components/CreativePreview';
import CreativeTypeFormComponent from 'scenes/Gallery/GalleryPage/Creatives/components/CreativeTypeField';
import {
  DesignerAndOutsourceStudioProvider,
  StudioType,
  StudioComponent,
} from 'scenes/Gallery/GalleryPage/Creatives/components/DesignerAndOutsourceStudio';
import EnhancerFormComponent from 'scenes/Gallery/GalleryPage/Creatives/components/EnhancersField';
import OSFormComponent from 'scenes/Gallery/GalleryPage/Creatives/components/OSField';
import * as CREATIVE from 'scenes/Gallery/GalleryPage/Creatives/constants';
import {
  loadRequestedBy,
  loadNetworks,
  loadLanguages,
  loadApplications,
  loadTags,
  loadInheritTags,
  loadExclusiveForAffiliates,
  loadTested,
  loadProductionTypes,
} from 'scenes/Gallery/GalleryPage/Creatives/sources/fields';
import * as VIEW_CREATIVE from 'scenes/Gallery/GalleryPage/Creatives/view-constants';
import {
  CREATIVE_TYPE_CINEMAGRAPH,
  CREATIVE_TYPE_VIDEO,
} from 'scenes/Gallery/GalleryPage/constants';
import { useGalleryPermissions } from 'scenes/Gallery/GalleryPage/hooks';

const EditCreativeForm = withForm({
  initialNormalized: false,
  enableReinitialize: true,
})(({ loading, values, onClose, onExited, open, entityId }) => {
  const {
    isAllowDesigner,
    isAllowOutsourceStudio,
    isAllowRequestedBy,
    isAllowJiraTask,
    isAllowExclusiveForAffiliates,
    isAllowTested,
    isAllowProductionType,
  } = useGalleryPermissions();

  const {
    [VIEW_CREATIVE.NAME]: name,
    [VIEW_CREATIVE.SHORT_NAME]: shortName,
    [CREATIVE.CREATIVE_TYPE]: creativeTypeId,
    [CREATIVE.FILE]: file,
  } = values || {};

  const subTitle = useMemo(() => {
    return [name, shortName].filter((i) => i).join(' / ');
  }, [name, shortName]);

  const isVisibleYoutubeLink = useMemo(() => {
    return [CREATIVE_TYPE_VIDEO, CREATIVE_TYPE_CINEMAGRAPH].includes(
      creativeTypeId
    );
  }, [creativeTypeId]);

  return (
    <SidePageV2
      onClose={onClose}
      onExited={onExited}
      open={open}
      loading={loading}
      size={'lg'}
    >
      <SidePageV2.Title
        title={'Edit Creative'}
        subTitle={subTitle}
        entityId={entityId}
      />
      <SidePageV2.NewBody dense={true}>
        <Section title={'Creative'}>
          <Grid container spacing={3}>
            <Grid item xs={6} container>
              <CreativePreview
                creativeType={creativeTypeId}
                fileInfo={file}
                maxWidth={426}
                maxHeight={356}
              />
            </Grid>
            <Grid item xs={6}>
              <Grid container spacing={3}>
                <Grid item xs={6}>
                  <CreativeTypeFormComponent
                    label={'Creative type'}
                    name={CREATIVE.CREATIVE_TYPE}
                    depends={[CREATIVE.FILE, CREATIVE.NETWORKS]}
                    required={true}
                    padding={0}
                  />
                </Grid>
                <Grid item xs={6}>
                  <ColorFormComponent
                    name={CREATIVE.COLOR}
                    creativeType={creativeTypeId}
                    padding={0}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormField.Select
                    label={'Application'}
                    name={CREATIVE.APPLICATION}
                    source={loadApplications}
                    depends={[CREATIVE.CONCEPT_ID]}
                    canDeselect={true}
                    padding={0}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormField.MultiSelect
                    label={'Networks'}
                    name={CREATIVE.NETWORKS}
                    source={loadNetworks}
                    depends={[CREATIVE.APPLICATION]}
                    required={true}
                    padding={0}
                  />
                </Grid>
                <Grid item xs={12}>
                  <ChannelsFormComponent
                    name={CREATIVE.CHANNELS}
                    creativeType={creativeTypeId}
                    padding={0}
                  />
                </Grid>
                {isAllowExclusiveForAffiliates ? (
                  <Grid item xs={12}>
                    <FormField.MultiSelect
                      label={'Exclusive for affiliates'}
                      name={CREATIVE.EXCLUSIVE_FOR_AFFILIATES}
                      source={loadExclusiveForAffiliates}
                      padding={0}
                    />
                  </Grid>
                ) : null}
                <Grid item xs={6}>
                  <OSFormComponent
                    name={CREATIVE.OS}
                    creativeType={creativeTypeId}
                    padding={0}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormField.Select
                    label={'Language'}
                    name={CREATIVE.LANGUAGE}
                    source={loadLanguages}
                    required={true}
                    padding={0}
                  />
                </Grid>
                {isAllowProductionType ? (
                  <Grid item xs={6}>
                    <FormField.Select
                      label={'Production type'}
                      name={CREATIVE.PRODUCTION_TYPE}
                      source={loadProductionTypes}
                      required={true}
                      padding={0}
                    />
                  </Grid>
                ) : null}
                <Grid item xs={6}>
                  <EnhancerFormComponent
                    name={CREATIVE.ENHANCERS}
                    creativeType={creativeTypeId}
                    padding={0}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormField.Checkbox
                    label={'AI Generated'}
                    name={CREATIVE.AI_GENERATED}
                    padding={0}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <FormField.MultiSelect
                label={'Inherit Internal Tags'}
                name={CREATIVE.INHERIT_TAGS}
                source={loadInheritTags}
                depends={[CREATIVE.CONCEPT_ID]}
                padding={0}
              />
            </Grid>
            <Grid item xs={12}>
              <FormField.MultiSelect
                label={'Internal Tags'}
                name={CREATIVE.TAGS}
                source={loadTags}
                depends={[CREATIVE.CONCEPT_ID]}
                padding={0}
              />
            </Grid>
            {isAllowJiraTask && (
              <Grid item xs={6}>
                <FormField.TextField
                  label={'Jira task URL'}
                  name={CREATIVE.JIRA_TASK}
                  padding={0}
                />
              </Grid>
            )}
            {isAllowTested ? (
              <Grid item xs={3}>
                <FormField.Select
                  label={'Tested'}
                  name={CREATIVE.TESTED}
                  source={loadTested}
                  required={true}
                  padding={0}
                />
              </Grid>
            ) : null}
            {isAllowRequestedBy && (
              <Grid item xs={3}>
                <FormField.Select
                  label={'Requested by'}
                  name={CREATIVE.REQUESTED_BY}
                  source={loadRequestedBy}
                  required={true}
                  padding={0}
                />
              </Grid>
            )}
            <Grid item xs={6}>
              <FormField.TextField
                label={"Concept's name postfix"}
                name={CREATIVE.POSTFIX}
                helper={'Custom name for creative'}
                padding={0}
              />
            </Grid>
            <Grid item xs={6}></Grid>
            <Grid item xs={3}>
              <FormField.TextField
                label={'Sound ID'}
                name={CREATIVE.SOUND_ID}
                thousandSeparator={false}
                type={'number'}
                padding={0}
              />
            </Grid>
            <DesignerAndOutsourceStudioProvider>
              {isAllowDesigner && (
                <Grid item xs={3}>
                  <StudioType />
                </Grid>
              )}
              {isAllowOutsourceStudio && (
                <Grid item xs={6}>
                  <StudioComponent />
                </Grid>
              )}
            </DesignerAndOutsourceStudioProvider>
            <Grid item xs={12}>
              <FormField.TextField
                label={'Description'}
                name={CREATIVE.DESCRIPTION}
                multiline={true}
                rows={3}
                padding={0}
              />
            </Grid>
            {isVisibleYoutubeLink && (
              <Grid item xs={12}>
                <FormField.TextField
                  label={'YouTube URL'}
                  name={CREATIVE.YOUTUBE_LINK}
                  padding={0}
                />
              </Grid>
            )}
          </Grid>
        </Section>
      </SidePageV2.NewBody>
      <SidePageV2.Actions>
        <SidePageV2.CloseButton />
        <SidePageV2.SubmitButton />
      </SidePageV2.Actions>
    </SidePageV2>
  );
});

export default EditCreativeForm;
